import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { toast } from 'sonner';
import {
  MessageSquare,
  Copy,
  Check,
  RefreshCw,
  Eye,
  EyeOff,
  Plug,
  PlugZap,
  Settings,
  Shield,
  AlertCircle,
  CheckCircle2,
  Clock,
  Users,
  Link2,
  Link2Off,
  ExternalLink,
  User,
  Building2,
  BarChart3,
  Megaphone,
  Instagram,
  Plus,
  Trash2,
  GitBranch,
  Facebook as FacebookIcon,
  Bot,
  MessageCircle,
  ToggleLeft,
  Save,
  Target,
  Info,
  Calendar as CalendarIcon,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert';
import { launchEmbeddedSignupCoexistence, launchEmbeddedSignupCloudAPI } from '../lib/whatsappSDK';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configuración de proveedores
const providerConfig = {
  manychat: {
    name: 'Webhook',
    description: 'Recibe leads desde cualquier plataforma externa (ManyChat, N8N, Make, etc.)',
    icon: MessageSquare,
    color: 'bg-blue-500',
    lightColor: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    hasWebhook: true,
    hasOAuth: false
  },
  meta_ads: {
    name: 'Meta Ads',
    description: 'Métricas de campañas publicitarias de Facebook e Instagram',
    icon: Megaphone,
    color: 'bg-[#1877F2]',
    lightColor: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    hasWebhook: false,
    hasOAuth: true
  },
  facebook: {
    name: 'Facebook',
    description: 'Conexión con páginas de Facebook',
    icon: FacebookIcon,
    color: 'bg-[#1877F2]',
    lightColor: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    hasWebhook: false,
    hasOAuth: true
  },
  instagram: {
    name: 'Instagram',
    description: 'Conexión con cuentas de Instagram Business',
    icon: Instagram,
    color: 'bg-gradient-to-r from-purple-500 to-pink-500',
    lightColor: 'bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300',
    hasWebhook: false,
    hasOAuth: true
  },
  whatsapp: {
    name: 'WhatsApp Business',
    description: 'API de WhatsApp Business para mensajería',
    icon: MessageSquare,
    color: 'bg-green-500',
    lightColor: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    hasWebhook: true,
    hasOAuth: false
  }
};

// ── Tarjeta especial para Meta Ads (múltiples cuentas) ───────────────────────
const MetaAdsCard = ({ integrations, onConnect, onDisconnect, isOwn = true }) => {
  const config = providerConfig.meta_ads;
  const Icon = config.icon;
  const connectedAccounts = integrations.filter(i => i.status === 'connected');
  const hasConnected = connectedAccounts.length > 0;

  return (
    <Card className={`transition-all ${hasConnected ? 'border-green-200 dark:border-green-800' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-lg ${config.color} text-white`}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                {config.name}
                {hasConnected ? (
                  <Badge className="bg-green-500 text-white text-xs">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    {connectedAccounts.length === 1 ? 'Conectado' : `${connectedAccounts.length} cuentas`}
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-xs">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    No conectado
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>{config.description}</CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {hasConnected ? (
          <>
            {/* Lista de cuentas conectadas */}
            <div className="space-y-2">
              {connectedAccounts.map(acct => (
                <div key={acct.id} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground">Account ID:</span>
                        <span className="font-mono text-xs truncate ml-2">{acct.account_id}</span>
                      </div>
                      {acct.account_name && acct.account_name !== 'Meta Ads Account' && (
                        <div className="flex justify-between items-center text-sm mt-0.5">
                          <span className="text-muted-foreground">Nombre:</span>
                          <span className="text-xs truncate ml-2">{acct.account_name}</span>
                        </div>
                      )}
                      {acct.connected_at && (
                        <div className="flex justify-between items-center text-sm mt-0.5">
                          <span className="text-muted-foreground">Conectado:</span>
                          <span className="text-xs">{new Date(acct.connected_at).toLocaleDateString('es-MX')}</span>
                        </div>
                      )}
                      {acct.metadata?.last_sync_at && (
                        <div className="flex justify-between items-center text-sm mt-0.5">
                          <span className="text-muted-foreground">Última sync:</span>
                          <span className="text-xs">{new Date(acct.metadata.last_sync_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      )}
                    </div>
                    {isOwn && (
                      <Button
                        variant="destructive"
                        size="sm"
                        className="shrink-0 h-7 text-xs px-2"
                        onClick={() => onDisconnect(acct.id, 'meta_ads')}
                      >
                        <Link2Off className="h-3 w-3 mr-1" />
                        Desconectar
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Botón para añadir otra cuenta */}
            {isOwn && (
              <div className="pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onConnect(null, 'meta_ads')}
                >
                  <PlugZap className="h-4 w-4 mr-2" />
                  Añadir otra cuenta
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="p-4 border-2 border-dashed rounded-lg text-center">
            <Plug className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground mb-3">
              Conecta tu cuenta para sincronizar campañas
            </p>
            {isOwn && (
              <Button onClick={() => onConnect(null, 'meta_ads')}>
                <PlugZap className="h-4 w-4 mr-2" />
                Conectar con OAuth
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Destinos de flujo ─────────────────────────────────────────────────────────
const FLOW_DESTINATIONS = [
  { value: 'leads_seguros',          label: 'Leads de Seguros' },
  { value: 'inmuebles_leads',        label: 'Leads Inmobiliarios' },
  { value: 'inmuebles_prospectos',   label: 'Prospectos en Seguimiento' },
];

// ── Tarjeta especial para ManyChat (con flujos múltiples) ─────────────────────
const ManyChatCard = ({
  integration,
  flows,
  onConnect,
  onDisconnect,
  onRegenerateSecret,
  onAddFlow,
  onDeleteFlow,
  onRegenerateFlowSecret,
  onUpdateFlowDestination,
  showSecret,
  onToggleSecret,
  copiedField,
  onCopy,
  isOwn = true,
}) => {
  const config = providerConfig.manychat;
  const Icon = config.icon;
  const isConnected = integration?.status === 'connected';
  const [newFlowName, setNewFlowName] = useState('');
  const [newFlowDest, setNewFlowDest] = useState('leads_seguros');
  const [addingFlow, setAddingFlow] = useState(false);

  const handleAddFlow = () => {
    if (!newFlowName.trim()) { return; }
    onAddFlow(newFlowName.trim(), newFlowDest);
    setNewFlowName('');
    setNewFlowDest('leads_seguros');
    setAddingFlow(false);
  };

  if (!integration) return null;

  return (
    <Card className={`transition-all ${isConnected ? 'border-blue-200 dark:border-blue-800' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-lg ${config.color} text-white`}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                {config.name}
                {isConnected ? (
                  <Badge className="bg-green-500 text-white text-xs">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Conectado
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-xs">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    No conectado
                  </Badge>
                )}
                {flows.length > 0 && (
                  <Badge variant="outline" className="text-xs">
                    <GitBranch className="h-3 w-3 mr-1" />
                    {flows.length} flujo{flows.length !== 1 ? 's' : ''}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>{config.description}</CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {isConnected ? (
          <>
            {/* URL del Webhook */}
            <div className="space-y-3">
              {integration.webhook_url ? (
                <div>
                  <Label className="text-xs text-muted-foreground">URL del Webhook</Label>
                  <div className="flex gap-2 mt-1">
                    <Input value={integration.webhook_url} readOnly className="font-mono text-xs" />
                    <Button variant="outline" size="icon" onClick={() => onCopy(integration.webhook_url, `webhook-${integration.id}`)}>
                      {copiedField === `webhook-${integration.id}` ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Usa esta URL en ManyChat, N8N, Make u otras plataformas para enviar leads al CRM.
                  </p>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">El token y la URL se generarán al activar la integración.</p>
              )}
              {isOwn && (
                <Button variant="outline" size="sm" onClick={() => onRegenerateSecret(integration.id)}>
                  <RefreshCw className="h-4 w-4 mr-2" />Regenerar token
                </Button>
              )}
            </div>

            {/* Flujos múltiples */}
            {isOwn && (
              <>
                <Separator />
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Flujos de destino</p>
                    {!addingFlow && (
                      <Button variant="outline" size="sm" onClick={() => setAddingFlow(true)}>
                        <Plus className="h-4 w-4 mr-1" />Añadir flujo
                      </Button>
                    )}
                  </div>

                  {addingFlow && (
                    <div className="p-3 border rounded-lg space-y-2 mb-3 bg-muted/30">
                      <Input placeholder="Nombre del flujo (ej. Flujo Inmobiliario)" value={newFlowName} onChange={e => setNewFlowName(e.target.value)} className="text-sm" />
                      <Select value={newFlowDest} onValueChange={setNewFlowDest}>
                        <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {FLOW_DESTINATIONS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleAddFlow} className="bg-blue-600 hover:bg-blue-700">Crear flujo</Button>
                        <Button size="sm" variant="outline" onClick={() => setAddingFlow(false)}>Cancelar</Button>
                      </div>
                    </div>
                  )}

                  {flows.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-2">Sin flujos personalizados aún. El webhook principal envía a Leads de Seguros.</p>
                  ) : (
                    <div className="space-y-3">
                      {flows.map(flow => (
                        <div key={flow.id} className="p-3 border rounded-lg space-y-2 bg-muted/20">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <GitBranch className="h-4 w-4 text-blue-500" />
                              <span className="font-medium text-sm">{flow.flow_name}</span>
                              <Badge variant={flow.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                                {flow.status === 'active' ? 'Activo' : 'Inactivo'}
                              </Badge>
                            </div>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => onDeleteFlow(flow.id)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-muted-foreground">Destino:</span>
                              <Select value={flow.destination} onValueChange={v => onUpdateFlowDestination(flow.id, v)}>
                                <SelectTrigger className="h-6 text-xs border-0 p-0 pl-1 w-auto focus:ring-0 bg-transparent">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {FLOW_DESTINATIONS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground">Webhook URL</Label>
                              <div className="flex gap-1 mt-0.5">
                                <Input value={flow.webhook_url || ''} readOnly className="font-mono text-xs h-7" />
                                <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => onCopy(flow.webhook_url, `flow-url-${flow.id}`)}>
                                  {copiedField === `flow-url-${flow.id}` ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                                </Button>
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground">Clave Secreta</Label>
                              <div className="flex gap-1 mt-0.5">
                                <Input type={showSecret[`flow-${flow.id}`] ? "text" : "password"} value={flow.secret_key || ''} readOnly className="font-mono text-xs h-7" />
                                <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => onToggleSecret(`flow-${flow.id}`)}>
                                  {showSecret[`flow-${flow.id}`] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                                </Button>
                                <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => onCopy(flow.secret_key, `flow-secret-${flow.id}`)}>
                                  {copiedField === `flow-secret-${flow.id}` ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                                </Button>
                                <Button variant="outline" size="icon" className="h-7 w-7" title="Regenerar clave" onClick={() => onRegenerateFlowSecret(flow.id)}>
                                  <RefreshCw className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                            {flow.leads_received > 0 && (
                              <p className="text-xs text-green-600 font-medium">{flow.leads_received} lead(s) recibido(s)</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {isOwn && (
              <div className="pt-1">
                <Button variant="destructive" size="sm" onClick={() => onDisconnect(integration.id, integration.provider)}>
                  <Link2Off className="h-4 w-4 mr-2" />Desconectar
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="p-4 border-2 border-dashed rounded-lg text-center">
            <Plug className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground mb-3">Activa esta integración para recibir datos via webhook</p>
            {isOwn && (
              <Button onClick={() => onConnect(integration.id, integration.provider)}>
                <PlugZap className="h-4 w-4 mr-2" />Activar integración
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Tarjeta genérica para otros proveedores ───────────────────────────────────
const IntegrationCard = ({
  integration,
  onConnect,
  onDisconnect,
  onRegenerateSecret,
  showSecret,
  onToggleSecret,
  copiedField,
  onCopy,
  isOwn = true
}) => {
  const config = providerConfig[integration.provider] || providerConfig.manychat;
  const Icon = config.icon;
  const isConnected = integration.status === 'connected';

  return (
    <Card className={`transition-all ${isConnected ? 'border-green-200 dark:border-green-800' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-lg ${config.color} text-white`}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                {config.name}
                {isConnected ? (
                  <Badge className="bg-green-500 text-white text-xs">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Conectado
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-xs">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    No conectado
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>{config.description}</CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {isConnected ? (
          <>
            {/* Información de conexión */}
            {integration.account_id && (
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Account ID:</span>
                  <span className="font-mono text-xs">{integration.account_id}</span>
                </div>
                {integration.connected_at && (
                  <div className="flex justify-between items-center text-sm mt-1">
                    <span className="text-muted-foreground">Conectado:</span>
                    <span>{new Date(integration.connected_at).toLocaleDateString('es-MX')}</span>
                  </div>
                )}
                {integration.leads_received > 0 && (
                  <div className="flex justify-between items-center text-sm mt-1">
                    <span className="text-muted-foreground">Leads recibidos:</span>
                    <span className="font-bold text-green-600">{integration.leads_received}</span>
                  </div>
                )}
              </div>
            )}

            {/* Webhook URL y Secret Key (para ManyChat) */}
            {config.hasWebhook && integration.webhook_url && (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Webhook URL</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={integration.webhook_url}
                      readOnly
                      className="font-mono text-xs"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => onCopy(integration.webhook_url, `webhook-${integration.id}`)}
                    >
                      {copiedField === `webhook-${integration.id}` ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                {integration.secret_key && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Clave Secreta</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type={showSecret[integration.id] ? "text" : "password"}
                        value={integration.secret_key}
                        readOnly
                        className="font-mono text-xs"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => onToggleSecret(integration.id)}
                      >
                        {showSecret[integration.id] ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => onCopy(integration.secret_key, `secret-${integration.id}`)}
                      >
                        {copiedField === `secret-${integration.id}` ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Botones de acción */}
            <div className="flex gap-2 pt-2">
              {config.hasWebhook && isOwn && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onRegenerateSecret(integration.id)}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Regenerar clave
                </Button>
              )}
              {isOwn && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onDisconnect(integration.id, integration.provider)}
                >
                  <Link2Off className="h-4 w-4 mr-2" />
                  Desconectar
                </Button>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Estado no conectado */}
            <div className="p-4 border-2 border-dashed rounded-lg text-center">
              <Plug className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground mb-3">
                {config.hasOAuth
                  ? 'Conecta tu cuenta para sincronizar datos'
                  : 'Activa esta integración para recibir datos via webhook'
                }
              </p>
              {isOwn && (
                <Button onClick={() => onConnect(integration.id, integration.provider)}>
                  <PlugZap className="h-4 w-4 mr-2" />
                  {config.hasOAuth ? 'Conectar con OAuth' : 'Activar integración'}
                </Button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ── Tarjeta Facebook Messenger ────────────────────────────────────────────────
const MessengerCard = ({ connection, onConnect, onDisconnect, onBotSave }) => {
  const isConnected = !!connection;
  const [personality, setPersonality] = useState(connection?.bot_personality || '');
  const [botActive, setBotActive] = useState(connection?.bot_active ?? true);
  const [saving, setSaving] = useState(false);

  // Sync local state when connection changes (e.g., after reconnect)
  React.useEffect(() => {
    setPersonality(connection?.bot_personality || '');
    setBotActive(connection?.bot_active ?? true);
  }, [connection]);

  const handleSave = async () => {
    setSaving(true);
    await onBotSave({ bot_personality: personality, bot_active: botActive });
    setSaving(false);
  };

  return (
    <Card className={`transition-all ${isConnected ? 'border-blue-200 dark:border-blue-800' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-[#1877F2] text-white">
              <FacebookIcon className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                Facebook Messenger
                {isConnected ? (
                  <Badge className="bg-green-500 text-white text-xs">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Conectado
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-xs">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    No conectado
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>Bot IA que responde mensajes y genera leads automáticamente</CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {isConnected ? (
          <>
            {/* Info de la página conectada */}
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-1.5">
              {connection.page_name && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Página:</span>
                  <span className="font-medium truncate ml-2">{connection.page_name}</span>
                </div>
              )}
              {connection.page_id && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Page ID:</span>
                  <span className="font-mono text-xs">{connection.page_id}</span>
                </div>
              )}
              {connection.created_at && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Conectado:</span>
                  <span className="text-xs">{new Date(connection.created_at).toLocaleDateString('es-MX')}</span>
                </div>
              )}
              <div className="flex justify-between items-center text-sm pt-1 border-t">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Target className="h-3.5 w-3.5" />
                  Leads generados:
                </span>
                <span className="font-bold text-green-600">{connection.leads_generated ?? 0}</span>
              </div>
            </div>

            {/* Toggle bot activo */}
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-center gap-2">
                <Bot className="h-4 w-4 text-blue-500" />
                <div>
                  <p className="text-sm font-medium">Bot IA activo</p>
                  <p className="text-xs text-muted-foreground">
                    {botActive ? 'Respondiendo mensajes automáticamente' : 'Respuestas automáticas desactivadas'}
                  </p>
                </div>
              </div>
              <Switch
                checked={botActive}
                onCheckedChange={setBotActive}
              />
            </div>

            {/* Personalidad del bot */}
            <div className="space-y-2">
              <Label className="text-sm flex items-center gap-1.5">
                <MessageCircle className="h-3.5 w-3.5" />
                Personalidad del bot
              </Label>
              <Textarea
                value={personality}
                onChange={e => setPersonality(e.target.value)}
                placeholder={`Eres un asistente de ventas amable de EstraPat. Ayudas a los prospectos a conocer nuestros productos de seguros. Siempre preguntas el nombre y teléfono del prospecto...`}
                className="text-sm min-h-[100px] resize-none"
                maxLength={2000}
              />
              <p className="text-xs text-muted-foreground text-right">{personality.length}/2000</p>
            </div>

            {/* Botones */}
            <div className="flex gap-2 pt-1">
              <Button size="sm" onClick={handleSave} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Guardando…' : 'Guardar configuración'}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={onDisconnect}
              >
                <Link2Off className="h-4 w-4 mr-2" />
                Desconectar
              </Button>
            </div>
          </>
        ) : (
          <div className="p-4 border-2 border-dashed rounded-lg text-center">
            <FacebookIcon className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground mb-1">
              Conecta tu página de Facebook para activar el bot de Messenger
            </p>
            <p className="text-xs text-muted-foreground mb-3">
              Los mensajes entrantes crearán leads automáticamente y el bot de IA responderá por ti
            </p>
            <Button onClick={onConnect}>
              <PlugZap className="h-4 w-4 mr-2" />
              Conectar con OAuth
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Tarjeta Instagram DMs ──────────────────────────────────────────────────────
const InstagramCard = ({ connection, onConnect, onDisconnect, onBotSave }) => {
  const isConnected = !!connection;
  const [personality, setPersonality] = useState(connection?.bot_personality || '');
  const [botActive, setBotActive] = useState(connection?.bot_active ?? true);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    setPersonality(connection?.bot_personality || '');
    setBotActive(connection?.bot_active ?? true);
  }, [connection]);

  const handleSave = async () => {
    setSaving(true);
    await onBotSave({ bot_personality: personality, bot_active: botActive });
    setSaving(false);
  };

  return (
    <Card className={`transition-all ${isConnected ? 'border-purple-200 dark:border-purple-800' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg text-white" style={{ background: 'linear-gradient(135deg, #833ab4 0%, #fd1d1d 50%, #fcb045 100%)' }}>
              <Instagram className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                Instagram DMs
                {isConnected ? (
                  <Badge className="bg-green-500 text-white text-xs">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Conectado
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-xs">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    No conectado
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>Bot IA que responde mensajes directos y genera leads automáticamente</CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {isConnected ? (
          <>
            {/* Info de la cuenta conectada */}
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-1.5">
              {connection.page_name && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Cuenta:</span>
                  <span className="font-medium truncate ml-2">@{connection.page_name}</span>
                </div>
              )}
              {connection.page_id && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Account ID:</span>
                  <span className="font-mono text-xs">{connection.page_id}</span>
                </div>
              )}
              {connection.created_at && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Conectado:</span>
                  <span className="text-xs">{new Date(connection.created_at).toLocaleDateString('es-MX')}</span>
                </div>
              )}
              <div className="flex justify-between items-center text-sm pt-1 border-t">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Target className="h-3.5 w-3.5" />
                  Leads generados:
                </span>
                <span className="font-bold text-green-600">{connection.leads_generated ?? 0}</span>
              </div>
            </div>

            {/* Toggle bot activo */}
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-center gap-2">
                <Bot className="h-4 w-4 text-purple-500" />
                <div>
                  <p className="text-sm font-medium">Bot IA activo</p>
                  <p className="text-xs text-muted-foreground">
                    {botActive ? 'Respondiendo mensajes automáticamente' : 'Respuestas automáticas desactivadas'}
                  </p>
                </div>
              </div>
              <Switch checked={botActive} onCheckedChange={setBotActive} />
            </div>

            {/* Personalidad del bot */}
            <div className="space-y-2">
              <Label className="text-sm flex items-center gap-1.5">
                <MessageCircle className="h-3.5 w-3.5" />
                Personalidad del bot
              </Label>
              <Textarea
                value={personality}
                onChange={e => setPersonality(e.target.value)}
                placeholder={`Eres un asistente de ventas amable de EstraPat. Ayudas a los prospectos a conocer nuestros productos de seguros. Siempre preguntas el nombre y teléfono del prospecto...`}
                className="text-sm min-h-[100px] resize-none"
                maxLength={2000}
              />
              <p className="text-xs text-muted-foreground text-right">{personality.length}/2000</p>
            </div>

            {/* Botones */}
            <div className="flex gap-2 pt-1">
              <Button size="sm" onClick={handleSave} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Guardando…' : 'Guardar configuración'}
              </Button>
              <Button variant="destructive" size="sm" onClick={onDisconnect}>
                <Link2Off className="h-4 w-4 mr-2" />
                Desconectar
              </Button>
            </div>
          </>
        ) : (
          <div className="p-4 border-2 border-dashed rounded-lg text-center">
            <Instagram className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground mb-1">
              Conecta tu cuenta de Instagram Business para activar el bot de DMs
            </p>
            <p className="text-xs text-muted-foreground mb-3">
              Los mensajes directos crearán leads automáticamente y el bot de IA responderá por ti
            </p>
            <Button onClick={onConnect}>
              <PlugZap className="h-4 w-4 mr-2" />
              Conectar con OAuth
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Tarjeta WhatsApp Business ──────────────────────────────────────────────────
const WhatsAppCard = ({
  connection, whatsappNumbers = [],
  onConnect, onDisconnect, onBotSave,
  onNumberDisconnect, onNumberBotSave,
  onStartEmbeddedSignup,
  isAdmin = false,
}) => {
  // ── Sección 1 — Embedded Signup ──────────────────────────────────────────────
  const [modeModalOpen, setModeModalOpen] = useState(false);

  // Modal configuración de bot por número
  const [configNumber, setConfigNumber]             = useState(null);
  const [configPersonality, setConfigPersonality]   = useState('');
  const [configBotActive, setConfigBotActive]       = useState(true);
  const [configPauseMinutes, setConfigPauseMinutes] = useState(30);
  const [savingConfig, setSavingConfig]             = useState(false);

  const openConfig = (num) => {
    setConfigNumber(num);
    setConfigPersonality(num.personality || '');
    setConfigBotActive(num.bot_activo ?? true);
    setConfigPauseMinutes(num.bot_pause_minutes ?? 30);
  };

  const handleConfigSave = async () => {
    setSavingConfig(true);
    const payload = { bot_activo: configBotActive, personality: configPersonality };
    if (configNumber?.connection_mode === 'coexistence') {
      payload.bot_pause_minutes = Number(configPauseMinutes);
    }
    await onNumberBotSave(configNumber.id, payload);
    setSavingConfig(false);
    setConfigNumber(null);
  };

  // ── Sección 2 — Modo desarrollo (formulario manual) ──────────────────────────
  const isConnected = !!connection;
  const [personality, setPersonality]   = useState(connection?.bot_personality || '');
  const [botActive, setBotActive]       = useState(connection?.bot_active ?? true);
  const [saving, setSaving]             = useState(false);
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [accessToken, setAccessToken]   = useState('');
  const [displayPhone, setDisplayPhone] = useState('');
  const [wabaId, setWabaId]             = useState('');
  const [verifyToken, setVerifyToken]   = useState('');
  const [connecting, setConnecting]     = useState(false);

  React.useEffect(() => {
    setPersonality(connection?.bot_personality || '');
    setBotActive(connection?.bot_active ?? true);
  }, [connection]);

  const handleDevSave = async () => {
    setSaving(true);
    await onBotSave({ bot_personality: personality, bot_active: botActive });
    setSaving(false);
  };

  const canConnect = phoneNumberId.trim() && accessToken.trim() && wabaId.trim() && verifyToken.trim();

  const handleDevConnect = async () => {
    if (!canConnect) return;
    setConnecting(true);
    await onConnect({
      phone_number_id: phoneNumberId.trim(),
      access_token:    accessToken.trim(),
      display_phone:   displayPhone.trim(),
      waba_id:         wabaId.trim(),
      verify_token:    verifyToken.trim(),
    });
    setConnecting(false);
  };

  const hasEmbedded = whatsappNumbers.length > 0;

  return (
    <>
      <Card className={`transition-all ${hasEmbedded ? 'border-green-200 dark:border-green-800' : ''}`}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-[#25D366] text-white">
                <MessageSquare className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  WhatsApp Business
                  {hasEmbedded ? (
                    <Badge className="bg-green-500 text-white text-xs">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      {whatsappNumbers.length} número{whatsappNumbers.length !== 1 ? 's' : ''} conectado{whatsappNumbers.length !== 1 ? 's' : ''}
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      No conectado
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>Bot IA que responde mensajes y genera leads automáticamente</CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">

          {/* ── Sección 1: Números conectados (Embedded Signup) ── */}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Números conectados
            </p>

            {whatsappNumbers.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">
                Aún no hay números conectados. Haz clic en "+ Conectar número" para comenzar.
              </p>
            ) : (
              <TooltipProvider>
                <div className="space-y-2">
                  {whatsappNumbers.map(num => {
                    const needsReconnect = !num.display_phone || num.display_phone === num.phone_number_id;
                    const isCoex = num.connection_mode === 'coexistence';
                    const botTooltip = num.bot_activo
                      ? (num.personality?.trim()
                          ? num.personality.trim().slice(0, 60) + (num.personality.trim().length > 60 ? '…' : '')
                          : 'Sin personalidad configurada')
                      : 'Bot desactivado';
                    return (
                      <div
                        key={num.id}
                        className="flex items-center justify-between p-3 border rounded-lg bg-gray-50 dark:bg-gray-800"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{num.nombre || 'Sin nombre'}</p>
                          {needsReconnect ? (
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <Badge variant="outline" className="text-xs text-amber-600 border-amber-400 py-0 px-1.5">
                                ⚠ Reconectar para ver número
                              </Badge>
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground">{num.display_phone}</p>
                          )}
                          <div className="flex items-center gap-1 mt-1 flex-wrap">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  {num.bot_activo ? (
                                    <Badge className="bg-green-500 text-white text-xs py-0 px-1.5 cursor-default">
                                      <Bot className="h-3 w-3 mr-1" />
                                      Bot activo
                                    </Badge>
                                  ) : (
                                    <Badge variant="secondary" className="text-xs py-0 px-1.5 cursor-default">
                                      <Bot className="h-3 w-3 mr-1" />
                                      Bot inactivo
                                    </Badge>
                                  )}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="bottom">
                                <p className="max-w-[200px] text-xs">{botTooltip}</p>
                              </TooltipContent>
                            </Tooltip>
                            {isCoex ? (
                              <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 text-xs py-0 px-1.5">
                                🔄 Coexistencia
                              </Badge>
                            ) : (
                              <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 text-xs py-0 px-1.5">
                                ☁️ Cloud API
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1.5 ml-2 shrink-0">
                          {needsReconnect && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs text-amber-600 border-amber-400 hover:bg-amber-50"
                              onClick={() => onNumberDisconnect(num.id)}
                            >
                              <RefreshCw className="h-3 w-3 mr-1" />
                              Reconectar
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => openConfig(num)}>
                            <Settings className="h-3 w-3 mr-1" />
                            Configurar
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive hover:text-destructive" onClick={() => onNumberDisconnect(num.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </TooltipProvider>
            )}

            <Alert className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800 py-2.5">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertTitle className="text-blue-800 dark:text-blue-200 text-xs font-semibold mb-1">
                Requisitos para conectar
              </AlertTitle>
              <AlertDescription className="text-blue-700 dark:text-blue-300">
                <ul className="list-disc list-inside space-y-0.5 text-xs">
                  <li>Cuenta de Meta Business Manager activa</li>
                  <li><strong>Coexistencia:</strong> WhatsApp Business v2.24.17+ en tu celular</li>
                  <li><strong>Cloud API:</strong> número no registrado en la app del celular</li>
                  <li>El admin debe haber configurado Login for Business en Meta App</li>
                </ul>
              </AlertDescription>
            </Alert>

            <Button
              onClick={() => setModeModalOpen(true)}
              className="w-full bg-[#25D366] hover:bg-[#1ebe5d] text-white"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              Conectar número de WhatsApp
            </Button>
          </div>

          {/* ── Sección 2: Modo desarrollo (acordeón colapsado) — solo admin ── */}
          {isAdmin && <Accordion type="single" collapsible className="border rounded-lg px-3">
            <AccordionItem value="dev" className="border-none">
              <AccordionTrigger className="text-xs font-medium text-amber-600 dark:text-amber-400 hover:no-underline py-3">
                ⚠️ Modo desarrollo — solo para pruebas
              </AccordionTrigger>
              <AccordionContent className="pt-0 pb-3">
                <div className="space-y-3">
                  {isConnected ? (
                    <>
                      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-1.5">
                        {connection.display_phone && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">Número:</span>
                            <span className="font-medium">{connection.display_phone}</span>
                          </div>
                        )}
                        {connection.phone_number_id && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">Phone Number ID:</span>
                            <span className="font-mono text-xs">{connection.phone_number_id}</span>
                          </div>
                        )}
                        {connection.waba_id && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">WABA ID:</span>
                            <span className="font-mono text-xs">{connection.waba_id}</span>
                          </div>
                        )}
                        {connection.verify_token && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">Verify Token:</span>
                            <span className="font-mono text-xs truncate max-w-[160px]">{connection.verify_token}</span>
                          </div>
                        )}
                        {connection.created_at && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">Conectado:</span>
                            <span className="text-xs">{new Date(connection.created_at).toLocaleDateString('es-MX')}</span>
                          </div>
                        )}
                        <div className="flex justify-between items-center text-sm pt-1 border-t">
                          <span className="text-muted-foreground flex items-center gap-1">
                            <Target className="h-3.5 w-3.5" />
                            Leads generados:
                          </span>
                          <span className="font-bold text-green-600">{connection.leads_generated ?? 0}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-2">
                          <Bot className="h-4 w-4 text-green-500" />
                          <div>
                            <p className="text-sm font-medium">Bot IA activo</p>
                            <p className="text-xs text-muted-foreground">
                              {botActive ? 'Respondiendo automáticamente' : 'Desactivado'}
                            </p>
                          </div>
                        </div>
                        <Switch checked={botActive} onCheckedChange={setBotActive} />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm flex items-center gap-1.5">
                          <MessageCircle className="h-3.5 w-3.5" />
                          Personalidad del bot
                        </Label>
                        <Textarea
                          value={personality}
                          onChange={e => setPersonality(e.target.value)}
                          placeholder="Eres un asistente de ventas amable de EstraPat..."
                          className="text-sm min-h-[80px] resize-none"
                          maxLength={2000}
                        />
                        <p className="text-xs text-muted-foreground text-right">{personality.length}/2000</p>
                      </div>

                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleDevSave} disabled={saving}>
                          <Save className="h-4 w-4 mr-2" />
                          {saving ? 'Guardando…' : 'Guardar'}
                        </Button>
                        <Button variant="destructive" size="sm" onClick={onDisconnect}>
                          <Link2Off className="h-4 w-4 mr-2" />
                          Desconectar
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-xs text-muted-foreground">
                        Ingresa las credenciales desde{' '}
                        <span className="font-medium">Meta for Developers</span>.
                      </p>
                      <div className="space-y-2">
                        <Label className="text-xs">Número de teléfono (display)</Label>
                        <Input value={displayPhone} onChange={e => setDisplayPhone(e.target.value)} placeholder="+52 55 1234 5678" className="text-sm h-8" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Phone Number ID *</Label>
                        <Input value={phoneNumberId} onChange={e => setPhoneNumberId(e.target.value)} placeholder="123456789012345" className="text-sm h-8 font-mono" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Access Token *</Label>
                        <Input type="password" value={accessToken} onChange={e => setAccessToken(e.target.value)} placeholder="EAA..." className="text-sm h-8 font-mono" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">WABA ID *</Label>
                        <Input value={wabaId} onChange={e => setWabaId(e.target.value)} placeholder="123456789012345" className="text-sm h-8 font-mono" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Verify Token *</Label>
                        <Input value={verifyToken} onChange={e => setVerifyToken(e.target.value)} placeholder="estrapat-whatsapp-verify-2026" className="text-sm h-8 font-mono" />
                      </div>
                      <Button onClick={handleDevConnect} disabled={connecting || !canConnect} className="w-full bg-[#25D366] hover:bg-[#1ebe5d] text-white" size="sm">
                        <PlugZap className="h-4 w-4 mr-2" />
                        {connecting ? 'Conectando…' : 'Conectar (modo dev)'}
                      </Button>
                    </>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>}

        </CardContent>
      </Card>

      {/* Dialog — configurar bot de un número */}
      <Dialog open={!!configNumber} onOpenChange={open => !open && setConfigNumber(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Configurar bot — {configNumber?.nombre}</DialogTitle>
            <DialogDescription>
              {configNumber?.display_phone || configNumber?.phone_number_id}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 border rounded-lg space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Configuración del Bot</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bot className={`h-4 w-4 ${configBotActive ? 'text-green-500' : 'text-gray-400'}`} />
                  <div>
                    <p className="text-sm font-medium">Bot IA</p>
                    <p className="text-xs text-muted-foreground">
                      {configBotActive ? 'Respondiendo automáticamente' : 'Desactivado — no responde mensajes'}
                    </p>
                  </div>
                </div>
                <Switch checked={configBotActive} onCheckedChange={setConfigBotActive} />
              </div>
            </div>
            <div className="space-y-2">
              <Label className={`text-sm flex items-center gap-1.5 ${!configBotActive ? 'text-muted-foreground' : ''}`}>
                <MessageCircle className="h-3.5 w-3.5" />
                Personalidad del bot
              </Label>
              <Textarea
                value={configPersonality}
                onChange={e => setConfigPersonality(e.target.value)}
                placeholder={configBotActive
                  ? "Eres un asistente de ventas amable de EstraPat. Ayudas a los prospectos a conocer nuestros productos de seguros..."
                  : "Activa el bot para configurar la personalidad"}
                className={`text-sm min-h-[100px] resize-none transition-colors ${!configBotActive ? 'bg-gray-100 dark:bg-gray-800 opacity-50 cursor-not-allowed' : 'bg-white dark:bg-gray-900'}`}
                disabled={!configBotActive}
                maxLength={2000}
              />
              <p className="text-xs text-muted-foreground text-right">{configPersonality.length}/2000</p>
            </div>
            {configNumber?.connection_mode === 'coexistence' && (
              <div className="space-y-1.5 p-3 border rounded-lg bg-green-50 dark:bg-green-950">
                <Label className="text-sm flex items-center gap-1.5 text-green-800 dark:text-green-200">
                  🔄 Pausa del bot tras respuesta del agente (minutos)
                </Label>
                <Input
                  type="number"
                  min={5}
                  max={180}
                  value={configPauseMinutes}
                  onChange={e => setConfigPauseMinutes(e.target.value)}
                  className="text-sm h-8 w-28"
                />
                <p className="text-xs text-green-700 dark:text-green-300">
                  Cuando respondas desde tu celular, el bot esperará este tiempo antes de retomar el control. Mín. 5 min, máx. 180 min.
                </p>
              </div>
            )}
            <div className="flex gap-2">
              <Button className="flex-1" onClick={handleConfigSave} disabled={savingConfig}>
                <Save className="h-4 w-4 mr-2" />
                {savingConfig ? 'Guardando…' : 'Guardar'}
              </Button>
              <Button variant="outline" onClick={() => setConfigNumber(null)} disabled={savingConfig}>
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog — seleccionar modo de conexión */}
      <Dialog open={modeModalOpen} onOpenChange={setModeModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-[#25D366] text-white">
                <MessageSquare className="h-4 w-4" />
              </div>
              ¿Cómo quieres conectar tu WhatsApp?
            </DialogTitle>
            <DialogDescription>
              Elige el modo que mejor se adapte a cómo usas WhatsApp en tu negocio.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {/* Coexistencia */}
            <button
              type="button"
              onClick={() => { setModeModalOpen(false); onStartEmbeddedSignup('coexistence'); }}
              className="w-full text-left p-4 border-2 rounded-xl hover:border-green-400 hover:bg-green-50 dark:hover:bg-green-950 transition-all group"
            >
              <div className="flex items-start justify-between mb-2">
                <p className="font-semibold text-sm">WhatsApp Business existente</p>
                <Badge className="bg-green-500 text-white text-xs ml-2 shrink-0">Recomendado</Badge>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Conecta el número que ya usas en tu celular sin perderlo.
              </p>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>✓ Sigue respondiendo desde tu celular</li>
                <li>✓ El bot te apoya cuando no estás disponible</li>
                <li>✓ No pierdes tu historial de chats</li>
                <li>✓ El bot pausa cuando tú respondes</li>
              </ul>
            </button>
            {/* Cloud API */}
            <button
              type="button"
              onClick={() => { setModeModalOpen(false); onStartEmbeddedSignup('cloud_api'); }}
              className="w-full text-left p-4 border-2 rounded-xl hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950 transition-all group"
            >
              <div className="flex items-start justify-between mb-2">
                <p className="font-semibold text-sm">Número exclusivo para CRM</p>
                <Badge className="bg-blue-500 text-white text-xs ml-2 shrink-0">Cloud API</Badge>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Un número dedicado solo para automatización.
              </p>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• Solo accesible desde el CRM</li>
                <li>• No funciona en la app del celular</li>
                <li>• Mayor throughput de mensajes</li>
              </ul>
            </button>
            <Button variant="outline" className="w-full" onClick={() => setModeModalOpen(false)}>
              Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ── Google Calendar Card ─────────────────────────────────────────────────────
const GoogleCalendarCard = ({ status, onConnect, onDisconnect }) => {
  const connected = status?.connected;
  return (
    <Card className={connected ? 'border-[#ea4335]/30' : ''}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center font-bold text-sm" style={{ color: '#ea4335' }}>
            G
          </div>
          Google Calendar
          {connected
            ? <Badge className="bg-green-100 text-green-700 border-green-200">Conectado</Badge>
            : <Badge variant="outline" className="text-muted-foreground">Sin conectar</Badge>}
        </CardTitle>
        <CardDescription>Sincroniza tu Google Calendar al CRM (solo lectura)</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {connected ? (
          <>
            <div className="flex items-center gap-2 text-sm p-2 bg-muted/40 rounded-lg">
              <CalendarIcon className="h-4 w-4 text-[#ea4335]" />
              <span className="font-medium">{status.calendar_name || 'Calendario principal'}</span>
            </div>
            {status.last_sync_at && (
              <p className="text-xs text-muted-foreground">
                Última sincronización: {new Date(status.last_sync_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Los eventos de Google se muestran en el Calendario del CRM con un badge rojo "G". Son de solo lectura.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 border-red-200 hover:bg-red-50 w-full"
              onClick={onDisconnect}
            >
              <Link2Off className="h-4 w-4 mr-2" />
              Desconectar Google Calendar
            </Button>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Conecta tu cuenta de Google para ver tus eventos del calendario en el CRM de forma automática.
            </p>
            <Button
              size="sm"
              className="w-full text-white hover:opacity-90"
              style={{ backgroundColor: '#ea4335' }}
              onClick={onConnect}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Conectar Google Calendar
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export const IntegrationsPage = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [myIntegrations, setMyIntegrations] = useState([]);
  const [otherIntegrations, setOtherIntegrations] = useState([]);
  const [showSecret, setShowSecret] = useState({});
  const [copiedField, setCopiedField] = useState(null);
  const [stats, setStats] = useState(null);
  const [manychatFlows, setManychatFlows] = useState([]);
  const [messengerConnection, setMessengerConnection] = useState(null);
  const [instagramConnection, setInstagramConnection] = useState(null);
  const [whatsappConnection, setWhatsappConnection]   = useState(null);
  const [whatsappNumbers, setWhatsappNumbers]         = useState([]);
  const [gcalStatus, setGcalStatus]                   = useState(null);
  // Meta Ads account selection after OAuth
  const [metaAccountsModal, setMetaAccountsModal] = useState(false);
  const [metaAccountsToken, setMetaAccountsToken] = useState('');
  const [metaAccountsList, setMetaAccountsList]   = useState([]);
  const [metaSelectedAccount, setMetaSelectedAccount] = useState('');
  const [metaSelectLoading, setMetaSelectLoading] = useState(false);

  const fetchIntegrations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/integrations`);
      setMyIntegrations(response.data.my_integrations || []);
      setOtherIntegrations(response.data.other_integrations || []);
    } catch (error) {
      console.error('Error al cargar integraciones:', error);
      toast.error('Error al cargar integraciones');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/integrations/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  }, []);

  const fetchFlows = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/integrations/manychat-flows`);
      setManychatFlows(res.data || []);
    } catch {}
  }, []);

  const fetchMessenger = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/integrations/messenger`);
      setMessengerConnection(res.data.connected ? res.data.connection : null);
    } catch {}
  }, []);

  const fetchInstagram = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/integrations/instagram`);
      setInstagramConnection(res.data.connected ? res.data.connection : null);
    } catch {}
  }, []);

  const fetchWhatsApp = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/integrations/whatsapp`);
      setWhatsappConnection(res.data.connected ? res.data.connection : null);
    } catch {}
  }, []);

  const fetchGcalStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/integrations/google-calendar/status`);
      setGcalStatus(res.data);
    } catch {}
  }, []);

  const fetchWhatsappNumbers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/whatsapp/numbers`);
      setWhatsappNumbers(res.data || []);
    } catch {}
  }, []);

  useEffect(() => {
    fetchIntegrations();
    fetchStats();
    fetchFlows();
    fetchMessenger();
    fetchInstagram();
    fetchWhatsApp();
    fetchWhatsappNumbers();
    fetchGcalStatus();
  }, [fetchIntegrations, fetchStats, fetchFlows, fetchMessenger, fetchInstagram, fetchWhatsApp, fetchWhatsappNumbers, fetchGcalStatus]);

  // Handle OAuth redirect params — same-tab redirect, user is already authenticated
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Meta Ads — account selection
    const metaAccountsToken = params.get('meta_accounts');
    const metaError = params.get('meta_error');
    if (metaAccountsToken) {
      try {
        // JWT payload is base64url — decode without verifying (backend verifies on POST)
        const b64 = metaAccountsToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(b64));
        setMetaAccountsToken(metaAccountsToken);
        setMetaAccountsList(payload.accounts || []);
        setMetaSelectedAccount(payload.accounts?.[0]?.id || '');
        setMetaAccountsModal(true);
      } catch {
        toast.error('Error al procesar cuentas de Meta. Intenta de nuevo.');
      }
      window.history.replaceState({}, '', window.location.pathname);
    } else if (metaError) {
      const metaErrorMessages = {
        no_accounts: 'No se encontraron cuentas publicitarias en este perfil de Meta.',
        token_exchange_failed: 'Error al intercambiar el código de autorización.',
        invalid_state: 'Sesión inválida. Por favor intenta de nuevo.',
        connection_failed: 'Error de conexión con Meta. Intenta de nuevo.',
        missing_params: 'Parámetros faltantes en la respuesta de Meta.',
      };
      toast.error(metaErrorMessages[metaError] || `Error Meta Ads: ${metaError}`, { duration: 8000 });
      window.history.replaceState({}, '', window.location.pathname);
    }

    const messengerParam = params.get('messenger');
    const messengerError = params.get('messenger_error');
    const messengerReason = params.get('reason');
    const instagramParam = params.get('instagram');
    const instagramError = params.get('instagram_error');

    if (messengerParam === 'connected') {
      fetchMessenger();
      toast.success('✅ Facebook Messenger conectado exitosamente');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (messengerParam === 'error') {
      const messages = {
        no_pages: 'No se encontraron páginas de Facebook asociadas. Asegúrate de seleccionar una página durante el proceso de autorización.',
        db_save_failed: 'Error al guardar la conexión. Intenta de nuevo.',
      };
      toast.error(messages[messengerReason] || `Error al conectar Messenger: ${messengerReason}`, { duration: 8000 });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (messengerError) {
      toast.error(`Error al conectar Messenger: ${messengerError}`);
      window.history.replaceState({}, '', window.location.pathname);
    }

    const gcalParam = params.get('gcal_connected');
    if (gcalParam === '1') {
      fetchGcalStatus();
      toast.success('✅ Google Calendar conectado exitosamente');
      window.history.replaceState({}, '', window.location.pathname);
    }

    const instagramReason = params.get('reason');
    if (instagramParam === 'connected') {
      fetchInstagram();
      toast.success('✅ Instagram conectado exitosamente');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (instagramParam === 'error') {
      const messages = {
        no_pages: 'No se encontraron páginas de Facebook asociadas a esta cuenta.',
        no_instagram_account: 'No se encontró una cuenta de Instagram Business vinculada a tu página de Facebook. Asegúrate de tener una cuenta de Instagram Business conectada.',
        db_save_failed: 'Error al guardar la conexión. Intenta de nuevo.',
      };
      toast.error(messages[instagramReason] || `Error al conectar Instagram: ${instagramReason}`, { duration: 8000 });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (instagramError) {
      toast.error(`Error al conectar Instagram: ${instagramError}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddFlow = async (flowName, destination) => {
    try {
      await axios.post(`${API}/integrations/manychat-flows`, { flow_name: flowName, destination });
      toast.success(`Flujo "${flowName}" creado`);
      fetchFlows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear flujo');
    }
  };

  const handleDeleteFlow = async (flowId) => {
    if (!window.confirm('¿Eliminar este flujo? El webhook dejará de funcionar.')) return;
    try {
      await axios.delete(`${API}/integrations/manychat-flows/${flowId}`);
      toast.success('Flujo eliminado');
      fetchFlows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar flujo');
    }
  };

  const handleRegenerateFlowSecret = async (flowId) => {
    if (!window.confirm('¿Regenerar la clave del flujo? La URL anterior dejará de funcionar.')) return;
    try {
      await axios.post(`${API}/integrations/manychat-flows/${flowId}/regenerate-secret`);
      toast.success('Clave del flujo regenerada');
      fetchFlows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al regenerar clave');
    }
  };

  const handleUpdateFlowDestination = async (flowId, destination) => {
    try {
      await axios.put(`${API}/integrations/manychat-flows/${flowId}`, { destination });
      setManychatFlows(prev => prev.map(f => f.id === flowId ? { ...f, destination } : f));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar destino');
    }
  };

  // ── Messenger handlers ──────────────────────────────────────────────────────
  const handleMessengerConnect = async () => {
    try {
      const res = await axios.get(`${API}/integrations/messenger/connect`);
      // Same-tab redirect — avoids popup auth issues; user returns to /integrations?messenger=connected
      window.location.href = res.data.auth_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al iniciar conexión con Messenger');
    }
  };

  const handleMessengerDisconnect = async () => {
    if (!window.confirm('¿Desconectar Facebook Messenger? El bot dejará de responder mensajes.')) return;
    try {
      await axios.delete(`${API}/integrations/messenger/disconnect`);
      setMessengerConnection(null);
      toast.success('Messenger desconectado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al desconectar');
    }
  };

  const handleMessengerBotSave = async (data) => {
    try {
      await axios.put(`${API}/integrations/messenger/bot`, data);
      setMessengerConnection(prev => ({ ...prev, ...data }));
      toast.success('Configuración del bot guardada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar configuración');
    }
  };

  // ── Instagram handlers ───────────────────────────────────────────────────────
  const handleInstagramConnect = async () => {
    try {
      const res = await axios.get(`${API}/integrations/instagram/connect`);
      window.location.href = res.data.auth_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al iniciar conexión con Instagram');
    }
  };

  const handleInstagramDisconnect = async () => {
    if (!window.confirm('¿Desconectar Instagram? El bot dejará de responder mensajes directos.')) return;
    try {
      await axios.delete(`${API}/integrations/instagram/disconnect`);
      setInstagramConnection(null);
      toast.success('Instagram desconectado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al desconectar');
    }
  };

  const handleInstagramBotSave = async (data) => {
    try {
      await axios.put(`${API}/integrations/instagram/bot`, data);
      setInstagramConnection(prev => ({ ...prev, ...data }));
      toast.success('Configuración del bot guardada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar configuración');
    }
  };

  // ── WhatsApp handlers ────────────────────────────────────────────────────────
  const handleWhatsAppConnect = async (credentials) => {
    try {
      await axios.post(`${API}/integrations/whatsapp/connect`, credentials);
      await fetchWhatsApp();
      toast.success('✅ WhatsApp Business conectado exitosamente');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al conectar WhatsApp');
    }
  };

  const handleWhatsAppDisconnect = async () => {
    if (!window.confirm('¿Desconectar WhatsApp Business? El bot dejará de responder mensajes.')) return;
    try {
      await axios.delete(`${API}/integrations/whatsapp/disconnect`);
      setWhatsappConnection(null);
      toast.success('WhatsApp desconectado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al desconectar');
    }
  };

  const handleWhatsAppBotSave = async (data) => {
    try {
      await axios.put(`${API}/integrations/whatsapp/bot`, data);
      setWhatsappConnection(prev => ({ ...prev, ...data }));
      toast.success('Configuración del bot guardada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar configuración');
    }
  };

  // ── WhatsApp Embedded Signup handlers ────────────────────────────────────────
  const handleWhatsappOAuthConnect = async (code, nombre, mode = 'cloud_api') => {
    try {
      await axios.post(`${API}/whatsapp/exchange-token`, { code, nombre, connection_mode: mode });
      await fetchWhatsappNumbers();
      const modeLabel = mode === 'coexistence' ? 'Coexistencia' : 'Cloud API';
      toast.success(`✅ Número de WhatsApp conectado en modo ${modeLabel}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errCode = typeof detail === 'object' ? detail?.error : null;
      const errMsg  = typeof detail === 'string'
        ? detail
        : (detail?.message || err.message || 'Error al conectar número de WhatsApp');
      if (errCode === 'whatsapp_pending') {
        toast.warning('Tu número de WhatsApp está en proceso de verificación con Meta. Intenta de nuevo cuando esté aprobado.');
      } else if (errCode === 'whatsapp_no_numbers') {
        toast.error('No se encontraron números de WhatsApp válidos en tu cuenta de Meta. Verifica que tengas un número configurado en WhatsApp Business Manager.');
      } else {
        toast.error(errMsg);
      }
    }
  };

  const handleWhatsappNumberDisconnect = async (numberId) => {
    if (!window.confirm('¿Desconectar este número? El bot dejará de responder mensajes enviados a él.')) return;
    try {
      await axios.delete(`${API}/whatsapp/numbers/${numberId}`);
      setWhatsappNumbers(prev => prev.filter(n => n.id !== numberId));
      toast.success('Número desconectado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al desconectar');
    }
  };

  const handleWhatsappNumberBotSave = async (numberId, data) => {
    try {
      await axios.patch(`${API}/whatsapp/numbers/${numberId}/bot`, data);
      setWhatsappNumbers(prev => prev.map(n => n.id === numberId ? { ...n, ...data } : n));
      toast.success('Configuración del bot guardada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar configuración');
    }
  };

  const handleStartEmbeddedSignup = async (mode) => {
    try {
      const { code } = mode === 'coexistence'
        ? await launchEmbeddedSignupCoexistence()
        : await launchEmbeddedSignupCloudAPI();

      const nombreVal = window.prompt('¿Qué nombre quieres darle a este número? (ej. Sucursal GDL, Ventas Norte)');
      if (!nombreVal?.trim()) {
        toast.info('Conexión cancelada');
        return;
      }

      await axios.post(`${API}/whatsapp/exchange-token`, {
        code,
        nombre: nombreVal.trim(),
        connection_mode: mode,
      });

      const modeLabel = mode === 'coexistence' ? 'Coexistencia' : 'Cloud API';
      toast.success(`✅ WhatsApp conectado en modo ${modeLabel}`);
      fetchWhatsappNumbers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errCode = typeof detail === 'object' ? detail?.error : null;
      const errMsg  = typeof detail === 'string'
        ? detail
        : (detail?.message || err.message || 'Error al conectar WhatsApp');
      if (errCode === 'whatsapp_pending') {
        toast.warning('Tu número de WhatsApp está en proceso de verificación con Meta. Intenta de nuevo cuando esté aprobado.');
      } else if (errCode === 'whatsapp_no_numbers') {
        toast.error('No se encontraron números de WhatsApp válidos en tu cuenta de Meta. Verifica tu configuración en WhatsApp Business Manager.');
      } else {
        toast.error(typeof errMsg === 'string' ? errMsg : 'Error al conectar WhatsApp');
      }
    }
  };

  const handleGcalConnect = async () => {
    try {
      const res = await axios.get(`${API}/integrations/google-calendar/connect`);
      window.location.href = res.data.auth_url;
    } catch (err) {
      if (err.response?.status === 503) {
        toast.info('Google Calendar pendiente de configuración. Contacta al administrador.');
      } else {
        toast.error('Error al iniciar conexión con Google Calendar');
      }
    }
  };

  const handleGcalDisconnect = async () => {
    if (!window.confirm('¿Desconectar Google Calendar? Los eventos de Google dejarán de aparecer en el CRM.')) return;
    try {
      await axios.delete(`${API}/integrations/google-calendar/disconnect`);
      setGcalStatus({ connected: false });
      toast.success('Google Calendar desconectado');
    } catch {
      toast.error('Error al desconectar Google Calendar');
    }
  };

  const handleMetaSelectAccount = async () => {
    if (!metaSelectedAccount) { toast.error('Selecciona una cuenta'); return; }
    setMetaSelectLoading(true);
    try {
      const res = await axios.post(`${API}/integrations/meta/select-account`, {
        token: metaAccountsToken,
        account_id: metaSelectedAccount,
      });
      toast.success(`✅ Meta Ads conectado: ${res.data.account_name}`);
      setMetaAccountsModal(false);
      fetchIntegrations();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar la cuenta');
    } finally {
      setMetaSelectLoading(false);
    }
  };

  const handleConnect = async (integrationId, provider) => {
    if (provider === 'meta_ads') {
      try {
        const res = await axios.get(`${API}/integrations/meta/connect`);
        // Same-tab redirect — user returns to /integrations?meta_accounts=<token>
        window.location.href = res.data.auth_url;
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al iniciar conexión con Meta');
      }
      return;
    }
    try {
      const response = await axios.post(`${API}/integrations/${integrationId}/connect`, {
        account_name: providerConfig[provider]?.name || 'Account'
      });
      toast.success(response.data.message || 'Integración conectada');
      fetchIntegrations();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al conectar');
    }
  };

  const handleDisconnect = async (integrationId, provider) => {
    if (!window.confirm('¿Estás seguro de desconectar esta cuenta?')) return;
    try {
      if (provider === 'meta_ads') {
        await axios.delete(`${API}/integrations/meta/disconnect`, {
          params: { integration_id: integrationId }
        });
      } else {
        await axios.post(`${API}/integrations/${integrationId}/disconnect`);
      }
      toast.success('Cuenta desconectada');
      fetchIntegrations();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al desconectar');
    }
  };

  const handleRegenerateSecret = async (integrationId) => {
    if (!window.confirm('¿Regenerar la clave secreta? La clave anterior dejará de funcionar.')) return;
    try {
      const response = await axios.post(`${API}/integrations/${integrationId}/regenerate-secret`);
      toast.success('Clave regenerada');
      fetchIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al regenerar');
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copiado al portapapeles');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const toggleSecret = (id) => {
    setShowSecret(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const myMetaAdsIntegrations   = myIntegrations.filter(i => i.provider === 'meta_ads');
  const myManyChatIntegration   = myIntegrations.find(i => i.provider === 'manychat');
  const myOtherIntegrations     = myIntegrations.filter(i => i.provider !== 'meta_ads' && i.provider !== 'manychat');
  const connectedCount = myIntegrations.filter(i => i.status === 'connected').length;

  return (
    <div className="space-y-6" data-testid="integrations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Integraciones</h1>
          <p className="text-muted-foreground">
            Conecta servicios externos para automatizar tu flujo de trabajo
          </p>
        </div>
        <Button variant="outline" onClick={() => { fetchIntegrations(); fetchStats(); }}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Stats para Admin */}
      {isAdmin && stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Total Integraciones</p>
                  <p className="text-2xl font-bold">{stats.total_integrations}</p>
                </div>
                <Plug className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Conectadas</p>
                  <p className="text-2xl font-bold text-green-600">{stats.connected}</p>
                </div>
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Sin conectar</p>
                  <p className="text-2xl font-bold text-gray-500">{stats.disconnected}</p>
                </div>
                <AlertCircle className="h-5 w-5 text-gray-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Usuarios con Integ.</p>
                  <p className="text-2xl font-bold">{stats.users_with_integrations || 0}</p>
                </div>
                <Users className="h-5 w-5 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Mis Integraciones */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 rounded-lg bg-[#1a365d] text-white">
                {isAdmin ? <Shield className="h-5 w-5" /> : <User className="h-5 w-5" />}
              </div>
              <div>
                <h2 className="text-xl font-semibold">
                  {isAdmin ? 'Integraciones del Administrador' : 'Mis Integraciones'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {connectedCount} de {myIntegrations.length} conectadas
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* ManyChat con flujos múltiples */}
              {myManyChatIntegration && (
                <ManyChatCard
                  integration={myManyChatIntegration}
                  flows={manychatFlows}
                  onConnect={handleConnect}
                  onDisconnect={handleDisconnect}
                  onRegenerateSecret={handleRegenerateSecret}
                  onAddFlow={handleAddFlow}
                  onDeleteFlow={handleDeleteFlow}
                  onRegenerateFlowSecret={handleRegenerateFlowSecret}
                  onUpdateFlowDestination={handleUpdateFlowDestination}
                  showSecret={showSecret}
                  onToggleSecret={toggleSecret}
                  copiedField={copiedField}
                  onCopy={copyToClipboard}
                  isOwn={true}
                />
              )}
              {/* Meta Ads */}
              <MetaAdsCard
                integrations={myMetaAdsIntegrations}
                onConnect={handleConnect}
                onDisconnect={handleDisconnect}
                isOwn={true}
              />
              {/* Facebook Messenger */}
              <MessengerCard
                connection={messengerConnection}
                onConnect={handleMessengerConnect}
                onDisconnect={handleMessengerDisconnect}
                onBotSave={handleMessengerBotSave}
              />
              {/* Instagram DMs */}
              <InstagramCard
                connection={instagramConnection}
                onConnect={handleInstagramConnect}
                onDisconnect={handleInstagramDisconnect}
                onBotSave={handleInstagramBotSave}
              />
              {/* Comentario → DM — solo si hay al menos una conexión de Messenger o Instagram */}
              {(messengerConnection || instagramConnection) && (
                <Card className="transition-all">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-lg bg-purple-600 text-white">
                        <MessageSquare className="h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">Comentario → DM</CardTitle>
                        <CardDescription>Respuestas automáticas a comentarios con palabra clave, tipo ManyChat</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Button size="sm" onClick={() => navigate('/integrations/comment-automation')}>
                      <Settings className="h-4 w-4 mr-2" />
                      Configurar publicaciones
                    </Button>
                  </CardContent>
                </Card>
              )}
              {/* WhatsApp Business — visible solo para admin (validación) */}
              {isAdmin && (
                <WhatsAppCard
                  connection={whatsappConnection}
                  whatsappNumbers={whatsappNumbers}
                  onConnect={handleWhatsAppConnect}
                  onDisconnect={handleWhatsAppDisconnect}
                  onBotSave={handleWhatsAppBotSave}
                  onNumberDisconnect={handleWhatsappNumberDisconnect}
                  onNumberBotSave={handleWhatsappNumberBotSave}
                  onStartEmbeddedSignup={handleStartEmbeddedSignup}
                  isAdmin={isAdmin}
                />
              )}
              {/* Google Calendar — disabled */}
              {false && (
                <GoogleCalendarCard
                  status={gcalStatus}
                  onConnect={handleGcalConnect}
                  onDisconnect={handleGcalDisconnect}
                />
              )}
              {/* Otras integraciones */}
              {myOtherIntegrations.map(integration => (
                <IntegrationCard
                  key={integration.id}
                  integration={integration}
                  onConnect={handleConnect}
                  onDisconnect={handleDisconnect}
                  onRegenerateSecret={handleRegenerateSecret}
                  showSecret={showSecret}
                  onToggleSecret={toggleSecret}
                  copiedField={copiedField}
                  onCopy={copyToClipboard}
                  isOwn={true}
                />
              ))}
            </div>
          </div>

          {/* Integraciones de otros usuarios (solo Admin) */}
          {isAdmin && otherIntegrations.length > 0 && (
            <>
              <Separator className="my-6" />
              
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-2 rounded-lg bg-gray-500 text-white">
                    <Users className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">Integraciones de Usuarios</h2>
                    <p className="text-sm text-muted-foreground">
                      {otherIntegrations.length} usuario(s) con integraciones configuradas
                    </p>
                  </div>
                </div>

                <Accordion type="single" collapsible className="space-y-2">
                  {otherIntegrations.map((userGroup) => {
                    const connectedCount = userGroup.integrations.filter(i => i.status === 'connected').length;
                    const roleLabel = userGroup.user_role === 'agente' ? 'Agente' : 
                                     userGroup.user_role === 'asesor' ? 'Asesor' : 
                                     userGroup.user_role === 'admin' ? 'Admin' : 'Usuario';
                    
                    return (
                      <AccordionItem 
                        key={userGroup.user_id} 
                        value={userGroup.user_id}
                        className="border rounded-lg px-4"
                      >
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-3 text-left">
                            <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-800">
                              <User className="h-4 w-4" />
                            </div>
                            <div>
                              <p className="font-medium">{userGroup.user_name || 'Usuario'}</p>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs capitalize">
                                  {roleLabel}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  {connectedCount}/{userGroup.integrations.length} conectadas
                                </span>
                              </div>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                            <MetaAdsCard
                              integrations={userGroup.integrations.filter(i => i.provider === 'meta_ads')}
                              onConnect={handleConnect}
                              onDisconnect={handleDisconnect}
                              isOwn={false}
                            />
                            {userGroup.integrations.filter(i => i.provider !== 'meta_ads').map(integration => (
                              <IntegrationCard
                                key={integration.id}
                                integration={integration}
                                onConnect={handleConnect}
                                onDisconnect={handleDisconnect}
                                onRegenerateSecret={handleRegenerateSecret}
                                showSecret={showSecret}
                                onToggleSecret={toggleSecret}
                                copiedField={copiedField}
                                onCopy={copyToClipboard}
                                isOwn={false}
                              />
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </div>
            </>
          )}

          {/* Mensaje cuando no hay integraciones de otros usuarios */}
          {isAdmin && otherIntegrations.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="p-8 text-center">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium mb-2">Sin integraciones de otros usuarios</h3>
                <p className="text-sm text-muted-foreground">
                  Cuando los agentes y asesores configuren sus integraciones, aparecerán aquí.
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Info footer */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-900 dark:text-blue-100">
                Seguridad de integraciones
              </p>
              <p className="text-blue-700 dark:text-blue-300">
                Las claves secretas y tokens de acceso están protegidos. Nunca compartas tus credenciales.
                {isAdmin && ' Como administrador, puedes ver y gestionar las integraciones de todos los usuarios.'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Meta Ads — selección de cuenta publicitaria ── */}
      <Dialog open={metaAccountsModal} onOpenChange={setMetaAccountsModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              Selecciona tu cuenta publicitaria
            </DialogTitle>
            <DialogDescription>
              Se encontraron {metaAccountsList.length} cuenta{metaAccountsList.length !== 1 ? 's' : ''} en tu perfil de Meta.
              Elige cuál conectar al CRM.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2 max-h-72 overflow-y-auto">
            {metaAccountsList.map(acc => (
              <div
                key={acc.id}
                onClick={() => setMetaSelectedAccount(acc.id)}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  metaSelectedAccount === acc.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-border hover:bg-muted/40'
                }`}
              >
                <div className={`h-4 w-4 rounded-full border-2 shrink-0 flex items-center justify-center ${
                  metaSelectedAccount === acc.id ? 'border-blue-500' : 'border-muted-foreground'
                }`}>
                  {metaSelectedAccount === acc.id && (
                    <div className="h-2 w-2 rounded-full bg-blue-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{acc.name || acc.id}</p>
                  <p className="text-xs text-muted-foreground">{acc.id}</p>
                </div>
                {acc.account_status === 1 && (
                  <Badge variant="outline" className="text-xs text-green-700 border-green-300 shrink-0">Activa</Badge>
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => setMetaAccountsModal(false)} disabled={metaSelectLoading}>
              Cancelar
            </Button>
            <Button
              onClick={handleMetaSelectAccount}
              disabled={!metaSelectedAccount || metaSelectLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {metaSelectLoading ? 'Conectando…' : 'Conectar cuenta'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IntegrationsPage;
